import type { Condition, DifferenceKind, GradeBand, Outcome, RuleSet } from './settingsApi';

export type CriteriaDifference = {
  key: string;
  bandKey: string;
  assessment: GradeBand['assessment'];
  grades: 'all' | string[];
  outcome: Outcome;
  measure: string;
  kind: DifferenceKind;
  recordMinimum?: number;
  districtMinimum?: number;
};

export const GRADE_ORDER = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

export function coveredGrades(band: GradeBand): 'all' | string[] {
  const covered = GRADE_ORDER.filter((g) => band.grades === 'all' || band.grades.includes(g));
  return covered.length === GRADE_ORDER.length ? 'all' : covered;
}

function gradeRuns(grades: 'all' | string[]): string {
  if (grades === 'all') return 'all';
  const indexes = grades.map((g) => GRADE_ORDER.indexOf(g)).filter((i) => i >= 0).sort((a, b) => a - b);
  const runs: string[] = [];
  let start = -1;
  let prev = -1;
  for (const i of indexes) {
    if (start >= 0 && i !== prev + 1) {
      runs.push(start === prev ? GRADE_ORDER[start]! : `${GRADE_ORDER[start]}-${GRADE_ORDER[prev]}`);
      start = i;
    }
    if (start < 0) start = i;
    prev = i;
  }
  if (start >= 0) runs.push(start === prev ? GRADE_ORDER[start]! : `${GRADE_ORDER[start]}-${GRADE_ORDER[prev]}`);
  return runs.join(',');
}

export function bandKey(band: GradeBand): string {
  return `${band.assessment}|${gradeRuns(coveredGrades(band))}`;
}

export function differenceKey(band: string, outcome: Outcome, measure: string): string {
  return `${band}|${outcome}|${measure}`;
}

export function canonicalBands(bands: GradeBand[]): string {
  return JSON.stringify(
    [...bands].sort((a, b) => bandKey(a).localeCompare(bandKey(b))).map((b) => ({
      assessment: b.assessment,
      grades: coveredGrades(b),
      noExit: b.noExit,
      ruleSets: [...b.ruleSets]
        .sort((x, y) => x.outcome.localeCompare(y.outcome))
        .map((r) => ({
          outcome: r.outcome,
          conditions: [...r.conditions].sort((x, y) => x.measure.localeCompare(y.measure)).map((c) => ({ measure: c.measure, minimum: c.minimum })),
        })),
    }))
  );
}

function ruleOf(band: GradeBand | undefined, outcome: Outcome): RuleSet | undefined {
  return band?.ruleSets.find((r) => r.outcome === outcome);
}

function conditionOf(rule: RuleSet | undefined, measure: string): Condition | undefined {
  return rule?.conditions.find((c) => c.measure === measure);
}

function districtSide(band: GradeBand, record: GradeBand | undefined): CriteriaDifference[] {
  const key = bandKey(band);
  const grades = coveredGrades(band);
  const out: CriteriaDifference[] = [];
  for (const rule of band.ruleSets) {
    const recordRule = ruleOf(record, rule.outcome);
    for (const c of rule.conditions) {
      const recordCondition = conditionOf(recordRule, c.measure);
      const base = { key: differenceKey(key, rule.outcome, c.measure), bandKey: key, assessment: band.assessment, grades, outcome: rule.outcome, measure: c.measure, districtMinimum: c.minimum };
      if (!recordRule) out.push({ ...base, kind: 'rule_added' });
      else if (!recordCondition) out.push({ ...base, kind: 'added' });
      else if (recordCondition.minimum !== c.minimum) out.push({ ...base, kind: 'changed', recordMinimum: recordCondition.minimum });
    }
  }
  return out;
}

function removedSide(record: GradeBand, district: GradeBand | undefined): CriteriaDifference[] {
  const key = bandKey(record);
  const grades = coveredGrades(record);
  const out: CriteriaDifference[] = [];
  for (const rule of record.ruleSets) {
    const districtRule = ruleOf(district, rule.outcome);
    for (const c of rule.conditions) {
      if (conditionOf(districtRule, c.measure)) continue;
      out.push({ key: differenceKey(key, rule.outcome, c.measure), bandKey: key, assessment: record.assessment, grades, outcome: rule.outcome, measure: c.measure, kind: 'removed', recordMinimum: c.minimum });
    }
  }
  return out;
}

export function criteriaDifferences(record: GradeBand[], district: GradeBand[]): CriteriaDifference[] {
  const recordByKey = new Map(record.map((b) => [bandKey(b), b]));
  const districtByKey = new Map(district.map((b) => [bandKey(b), b]));
  return [
    ...district.flatMap((b) => districtSide(b, recordByKey.get(bandKey(b)))),
    ...record.flatMap((b) => removedSide(b, districtByKey.get(bandKey(b)))),
  ];
}
